import {gql} from '@apollo/client';

import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import {
  EntityMap,
  EntityMap_entityMap_EntityMapNode_entities_EntityMapEntityNode as EntityMapEntityNode,
  EntityMap_entityMap_EntityMapNode_edges_EntityMapEdgeNode as EntityMapEdgeNode,
  EntityMapVariables,
} from './__generated/EntityMap';

export const ENTITY_MAP_QUERY = gql`
  query EntityMap($entityMapInput: EntityMapInputNode!) @skipAudit @engComponent(name: REGISTRY) {
    entityMap(entityMapInput: $entityMapInput) {
      ... on EntityMapNode {
        entities {
          id
          schemaName
        }
        edges {
          id
          sourceId
          targetId
        }
      }
    }
  }
`;

/**
 * Fetches the entity map graph for a given entityId and depth
 *
 * Throws an error if the data is unavailable or inaccessible.
 * @param entityId  The ID of the node to fetch the entity map for
 * @param depth  The depth of the entity map to fetch
 */
export async function fetchEntityMapGraph(
  entityId: string,
  depth: number
): Promise<{
  entities: Array<EntityMapEntityNode>;
  edges: Array<EntityMapEdgeNode>;
}> {
  const result = await doQuerySafe<EntityMap, EntityMapVariables>(
    {query: ENTITY_MAP_QUERY},
    {variables: {entityMapInput: {entityId, depth}}}
  );

  if (result?.entityMap == null || isInaccessible(result.entityMap)) {
    throw new Error('This data is not available.');
  }

  return {
    entities: result.entityMap.entities ?? [],
    edges: result.entityMap.edges ?? [],
  };
}
